import React, {
  useEffect, useCallback, useState, useRef,
} from 'react';
import {
  Navigate, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import LearningPathway from 'src/components/learning-pathway-v2';
import { AssessmentOverlay } from 'src/components/credential/AssessmentOverlay';
import { CredentialCompletionPanel } from 'src/components/credential/CredentialCompletionPanel';
import { ComingSoonSections } from 'src/components/credential/ComingSoonSections';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import { useBundle } from '@amzn/react-arb-tools';
import { MessageBundle } from '@amzn/arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { useAppSelector } from 'src/redux/hooks';
import { useGetCustomerCredentialQuery } from 'src/redux/slices/api/su';
import { selectLearningPathwayById } from 'src/redux/slices/learningPathways';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import { selectAllModulesComplete, selectCompletedModuleCount } from 'src/redux/slices/learningModules';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { AppState } from 'src/redux/store';
import ClientLogger from 'src/utils/logger';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { removeSearchParam } from 'src/utils/searchParams';
import { extractRefTag, validateRefTag } from 'src/utils/refParameterConversion';
import { getStatusCodeFromError } from 'src/utils/error';
import { AssessmentSummary } from 'src/types/api/credential';

export const buildAssessmentOverlayCopy = (
  credentialBundle: MessageBundle,
  assessment?: AssessmentSummary,
) => ({
  // Config-driven modal title: SUOS sends the assessment title as a credential-bundle key id.
  // Resolve it purely from the backend value via t(); the `?? ''` is a type-safety coalesce only.
  title: t(credentialBundle, assessment?.title ?? ''),
  questionProgressLabel: (current: number, total: number) => t(
    credentialBundle,
    CREDENTIAL_STRING_ID.ASSESSMENT_QUESTION_COUNTER,
    { current, total },
  ),
  backLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_BACK),
  continueLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_CONTINUE),
  submitLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_SUBMIT),
  closeLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_CLOSE),
  retakeLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_NAV_RETAKE),
  downloadCertificationLabel: t(credentialBundle, CREDENTIAL_STRING_ID.DOWNLOAD_CERTIFICATE_CTA),
  savingProgressLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_SAVING_PROGRESS),
  progressSavedLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_PROGRESS_SAVED),
  saveFailedLabel: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_SAVE_FAILED),
  passedTitle: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_PASSED_TITLE),
  failedTitle: t(credentialBundle, CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_FAILED_TITLE),
  // "N out of M correct" score line. The overlay derives the correct count client-side (the Submit
  // response returns only scorePercent), so this label just formats the two numbers it is given.
  correctCountLabel: (correct: number, total: number) => t(
    credentialBundle,
    CREDENTIAL_STRING_ID.ASSESSMENT_RESULT_SCORE,
    { correct, total },
  ),
  // Config-driven result copy: SUOS sends per-credential bundle-key ids for the pass/fail bodies.
  // Resolve them purely from the backend value via t(); the `?? ''` is only a type-safety coalesce
  // (t requires a string), not a copy fallback, so the overlay renders exactly what SUOS configures.
  passedBody: t(credentialBundle, assessment?.passedMessage ?? ''),
  // The failed body interpolates the AUTHORITATIVE pass threshold (AssessmentSummary.passPercent)
  // rather than a hardcoded number, so the copy can never silently contradict the service config
  // when passPercent changes. Passing a param a message does not reference is harmless, so this is
  // safe even for failed-message keys that omit the {passPercent} placeholder.
  failedBody: t(credentialBundle, assessment?.failedMessage ?? '', { passPercent: assessment?.passPercent }),
  // SUOS sends the exact credential-bundle key as each question prompt / option value (LP-style
  // direct match), so we resolve it straight against the bundle via t(), exactly like the sibling
  // copy fields above and pageHeaderV2. After the SUOS config fix the backend always sends valid
  // bundle keys, so a miss is a genuine misconfiguration that t() rightly logs (TranslationNotFound)
  // while echoing the id back.
  resolveQuestionText: (promptId: string) => t(credentialBundle, promptId),
  resolveOptionText: (textId: string) => t(credentialBundle, textId),
});

const CredentialPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);
  const [searchParams] = useSearchParams();
  const { credentialId } = useParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  // Ingress attribution: which surface sent the seller here; 'direct' when no valid ref tag.
  const entrySource = validateRefTag(extractRefTag(searchParams)) ?? 'direct';
  const { viewMetric, clickMetric } = useMetrics();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isAssessmentEarned, setIsAssessmentEarned] = useState(false);
  // Guard each once-per-credential emit, keyed on credentialId so a reused component instance
  // (route change to another credential) re-emits, while reopening the modal or dep churn does not.
  const hasEmittedAssessmentIntro = useRef<string | undefined>(undefined);
  const hasEmittedAssessmentUnlocked = useRef<string | undefined>(undefined);
  const hasEmittedDetail = useRef<string | undefined>(undefined);
  // AssessmentStarted dedupe by attemptId, lifted here (parent stays mounted) so it survives
  // modal close/reopen and resume; a genuine retake (new attemptId) re-emits.
  const startedAssessmentAttemptIdRef = useRef<string | null>(null);
  const isCredentialsPilotT1 = useAppSelector(
    (state: AppState) => selectIsWeblabInT1(state, WeblabId.CredentialsPilotLaunch),
  );

  const {
    data: customerCredentialData,
    isLoading,
    isError,
    error,
    isUninitialized,
    refetch,
  } = useGetCustomerCredentialQuery(
    { credentialId: credentialId ?? '', clientId, selLocale },
    { skip: !isCredentialsPilotT1 || !credentialId },
  );
  const credential = customerCredentialData?.customerCredential?.credential;
  const learningPathway = credential?.learningPathway;
  const learningPathwayId = learningPathway?.learningPathwayId;
  const hasAward = Boolean(customerCredentialData?.customerCredential?.award);
  // Earned state is server-authoritative: it is driven by the persisted `award` from
  // getCustomerCredential (refetched after any submit). `isAssessmentEarned` is a transitional
  // stopgap for the M1 slim backend, which may not return the award immediately after submit, so
  // the seller's just-earned credential still unlocks. Remove once the backend returns the award
  // reliably on refetch.
  const isCredentialEarned = hasAward || isAssessmentEarned;

  // A scored assessment is terminal: the seller has consumed their attempt and the credential is
  // resolved. Treat it as a never-lock signal so a later client-side module-status regression
  // (e.g. reopening a completed module) can't re-lock an assessment the seller already finished.
  const isAssessmentScored = customerCredentialData?.customerCredential?.assessment?.state === 'SCORED';

  // Badge and certificate assets come from the credential definition. When the (M1 slim) model
  // omits them, they are left undefined so the UI hides the earned badge / certificate download
  // rather than substituting another credential's asset.
  const earnedBadgeSrc = credential?.badge?.imageUrl;
  const certificateUrl = credential?.certificateUrl;
  // Config-driven assessment metadata (SUOS AssessmentSummary). Each field renders purely from the
  // backend value; the `?? ''` coalesces are type-safety only (t()/imageSrc require a string), not
  // copy/asset fallbacks, so the tile reflects exactly what SUOS configures.
  const assessmentSummary = credential?.assessment;
  // questionCount is @required on the wire; the ?? 0 only satisfies the pre-load optional-chain
  // (credential is guaranteed present once we pass the learningPathwayId guard below).
  const assessmentQuestionCount = assessmentSummary?.questionCount ?? 0;
  const assessmentDescription = t(
    credentialBundle,
    assessmentSummary?.description ?? '',
  );
  const assessmentImageSrc = assessmentSummary?.imageUrl ?? '';
  const completionPanelMessage = t(
    credentialBundle,
    assessmentSummary?.earnedMessage ?? '',
  );

  // The seller has an in-flight attempt when the customer assessment reports IN_PROGRESS. A
  // not-started assessment carries no state (attemptsUsed 0), so it stays in the todo state.
  const isAssessmentInProgress = customerCredentialData?.customerCredential?.assessment?.state === 'IN_PROGRESS'
    && !isCredentialEarned;

  // A scored attempt that did not earn the credential is a failed attempt. Pass/fail is not
  // persisted on the assessment state (SCORED covers both), so it is derived from the award.
  // In the M1 window where the award has not propagated and a reload has dropped
  // isAssessmentEarned, a passed seller reads as failed here — consistently with the rest of the
  // page, which shows the unearned badge and hides the certificate until the award arrives.
  const hasFailedAttempt = isAssessmentScored && !isCredentialEarned;

  // The row offers whichever action matches the attempt state. Resume is distinct from the
  // in-modal Continue, which advances to the next question.
  let assessmentCtaStringId: string = CREDENTIAL_STRING_ID.ASSESSMENT_START_CTA;
  if (hasFailedAttempt) {
    assessmentCtaStringId = CREDENTIAL_STRING_ID.ASSESSMENT_NAV_RETAKE;
  } else if (isAssessmentInProgress) {
    assessmentCtaStringId = CREDENTIAL_STRING_ID.ASSESSMENT_RESUME_CTA;
  }

  // CredentialDetail status dimensions, defaulted so the metric always carries a bounded value.
  const assessmentState = customerCredentialData?.customerCredential?.assessment?.state ?? 'NONE';
  const attemptsUsed = customerCredentialData?.customerCredential?.assessment?.attemptsUsed ?? 0;

  // The page also renders ErrorView for two non-query conditions (missing credentialId, and a
  // successful fetch with no learningPathwayId). Fold them into the fault signal so page
  // performance counts them as faults rather than successful loads.
  const hasRenderError = !credentialId || (!isLoading && !isUninitialized && !learningPathwayId);

  // Non-pilot sellers are redirected below and the credential query is skipped, so there is no
  // real page load to measure; keep the hook in its not-loaded state so it never emits for them.
  usePagePerformance(
    FeatureName.CredentialPage,
    isLoading || !isCredentialsPilotT1,
    isError || hasRenderError,
    credentialId,
  );

  const pathway = useAppSelector(
    (state: AppState) => selectLearningPathwayById(state, learningPathwayId ?? ''),
  );
  const moduleIds = pathway?.data?.sections?.reduce<string[]>(
    (acc, section) => acc.concat(section.learningModuleIds),
    [],
  ) ?? [];
  const isAssessmentUnlocked = useAppSelector(
    (state: AppState) => selectAllModulesComplete(state, moduleIds),
  );
  const completedModuleCount = useAppSelector(
    (state: AppState) => selectCompletedModuleCount(state, moduleIds),
  );
  // Credential progress spans all modules PLUS the assessment, so the assessment is one extra
  // step in the denominator (3 modules = 75%, assessment earned = 100%). The assessment step is
  // earned when the seller passes the scored assessment.
  const totalSteps = moduleIds.length + 1;
  const completedSteps = completedModuleCount + (isCredentialEarned ? 1 : 0);
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  const learningModuleInPathway = learningPathway?.sections?.some(
    (section) => section.moduleList?.some((module) => module.moduleId === learningModuleId),
  );

  useEffect(() => {
    if (isCredentialsPilotT1 && !isLoading && !isError && !isUninitialized && learningPathwayId) {
      if (hasEmittedDetail.current === credentialId) return;
      hasEmittedDetail.current = credentialId;
      viewMetric({
        feature: FeatureName.CredentialDetail,
        metadata: {
          credentialId,
          pathwayId: learningPathwayId,
          clientId,
          path: location.pathname,
          assessmentState,
          attemptsUsed,
          entrySource,
        },
      });
    }
  }, [isCredentialsPilotT1, isLoading, isError, isUninitialized, learningPathwayId, viewMetric, credentialId, clientId, location.pathname, assessmentState, attemptsUsed, entrySource]);

  useEffect(() => {
    // AssessmentUnlocked (BRD row 7): observed on render, once per credential, only while unlocked, not earned, not scored.
    if (
      isCredentialsPilotT1 && !isLoading && !isError && !isUninitialized && learningPathwayId
      && isAssessmentUnlocked && !isCredentialEarned && !isAssessmentScored
      && hasEmittedAssessmentUnlocked.current !== credentialId
    ) {
      viewMetric({
        feature: FeatureName.AssessmentUnlocked,
        metadata: { credentialId },
      });
      hasEmittedAssessmentUnlocked.current = credentialId;
    }
  }, [isCredentialsPilotT1, isLoading, isError, isUninitialized, learningPathwayId, isAssessmentUnlocked, isCredentialEarned, isAssessmentScored, viewMetric, credentialId]);

  useEffect(() => {
    if (!isLoading && !isError && !isUninitialized && !learningPathwayId) {
      ClientLogger.error(ClientLog.PayloadProcessingError, {
        message: 'Learning pathway data unavailable after successful fetch',
        credentialId,
        learningPathwayId,
        clientId,
        url: window.location.href,
      });
    }
  }, [isLoading, isError, isUninitialized, learningPathwayId, credentialId, clientId]);

  useEffect(() => {
    if (!isError) {
      return;
    }
    const statusCode = getStatusCodeFromError(error);
    const logContext = {
      credentialId,
      learningPathwayId,
      clientId,
      path: location.pathname,
      url: window.location.href,
    };
    if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
      ClientLogger.warn(ClientLog.IllegalInternalDataState, logContext);
    } else {
      ClientLogger.error(ClientLog.IllegalInternalDataState, logContext);
    }
  }, [isError, error, credentialId, learningPathwayId, clientId, location.pathname]);

  useEffect(() => {
    if (!credentialId) {
      ClientLogger.error(ClientLog.IllegalInternalDataState, {
        message: `Expected credentialId to be present in browser URL path: ${location.pathname}`,
        path: location.pathname,
        url: window.location.href,
      });
    }
  }, [credentialId, location.pathname]);

  const appendModuleSearchParam = (moduleId: string) => {
    // Module opened from within the credential page = a credential-driven start (separable from standalone starts).
    clickMetric({
      feature: FeatureName.CredentialModuleStart,
      metadata: { moduleId, credentialId, learningPathwayId },
    });
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, moduleId);
    navigate({ search: updatedSearchParams.toString() }, { replace: true });
  };

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  // AssessmentStarted deduped by attemptId here (parent stays mounted), so a resumed attempt does
  // not re-emit across modal reopens while a retake (new attemptId) does.
  const handleAssessmentStarted = useCallback((attemptId: string) => {
    if (startedAssessmentAttemptIdRef.current !== attemptId) {
      startedAssessmentAttemptIdRef.current = attemptId;
      clickMetric({ feature: FeatureName.AssessmentStarted, metadata: { credentialId } });
    }
  }, [clickMetric, credentialId]);

  if (!isCredentialsPilotT1) {
    return <Navigate to={ROUTES.NEW_LEARN.BASE} replace />;
  }

  if (!credentialId) {
    return <ErrorView />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  if (isError) {
    return <ErrorView statusCode={getStatusCodeFromError(error)} />;
  }

  if (!learningPathwayId) {
    return <ErrorView />;
  }

  const resolvedLearningPathwayId = learningPathwayId;

  return (
    <div className="su-credential-page">
      <LearningPathway
        learningPathwayId={resolvedLearningPathwayId}
        onModuleSelect={appendModuleSearchParam}
        // A count (not a hardcoded 1) so the header copy pluralizes correctly if the credential ever
        // carries a list of assessments; the presence of the single assessment yields 1, its absence 0.
        assessmentCount={assessmentSummary ? 1 : 0}
        ribbon={t(credentialBundle, CREDENTIAL_STRING_ID.HOME_COURSE_CERTIFICATE_RIBBON)}
        progress={{
          percentage: completionPercentage,
          badge: earnedBadgeSrc
            ? {
              earned: isCredentialEarned,
              earnedSrc: earnedBadgeSrc,
              unearnedSrc: credential?.badge?.unearnedImageUrl ?? '',
              alt: t(credentialBundle, CREDENTIAL_STRING_ID.BADGE_ALT),
            }
            : undefined,
        }}
        hideSectionLabels
        enforceSerialProgression
        assessment={{
          title: t(credentialBundle, assessmentSummary?.title ?? ''),
          description: assessmentDescription,
          imageSrc: assessmentImageSrc,
          ctaLabel: t(credentialBundle, assessmentCtaStringId),
          questionCount: assessmentQuestionCount,
          locked: !isAssessmentUnlocked && !isCredentialEarned && !isAssessmentScored,
          earned: isCredentialEarned,
          // A failed attempt is reported as in-progress so the row shows an attempted state
          // rather than "Not started"; the CTA label distinguishes resume from re-take.
          inProgress: isAssessmentInProgress || hasFailedAttempt,
          onStart: () => {
            // No separate intro step: the intro is "viewed" when the modal opens. Once per credential.
            if (hasEmittedAssessmentIntro.current !== credentialId) {
              viewMetric({
                feature: FeatureName.AssessmentIntroViewed,
                metadata: { credentialId },
              });
              hasEmittedAssessmentIntro.current = credentialId;
            }
            setIsAssessmentModalOpen(true);
          },
        }}
      />
      {isCredentialEarned && certificateUrl && (
        <CredentialCompletionPanel
          message={completionPanelMessage}
          downloadLabel={t(credentialBundle, CREDENTIAL_STRING_ID.DOWNLOAD_CERTIFICATE_CTA)}
          certificateUrl={certificateUrl}
          credentialId={credentialId}
          ctaLink={credential?.ctaLink}
          ctaLabel={credential?.ctaText ? t(credentialBundle, credential.ctaText) : undefined}
        />
      )}
      {isAssessmentModalOpen && (
        <AssessmentOverlay
          credentialId={credentialId}
          clientId={clientId}
          selLocale={selLocale}
          copy={buildAssessmentOverlayCopy(credentialBundle, assessmentSummary)}
          completionCertificateUrl={certificateUrl}
          onClose={() => setIsAssessmentModalOpen(false)}
          onAssessmentStarted={handleAssessmentStarted}
          onSubmitted={refetch}
          onPassed={() => {
            setIsAssessmentEarned(true);
            viewMetric({
              feature: FeatureName.CredentialEarned,
              metadata: { credentialId, clientId, path: location.pathname },
            });
          }}
        />
      )}
      <ComingSoonSections credentialId={credentialId} />
      {learningModuleId && learningModuleInPathway && (
        <LearningModuleModal
          learningModuleId={learningModuleId}
          onModalClose={removeModuleSearchParam}
          parentId={resolvedLearningPathwayId}
          parentType={EntityType.LearningPathway}
        />
      )}
    </div>
  );
};

export default CredentialPage;
